import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  ChevronLeft, ChevronRight, Upload, CheckCircle, X, Zap,
  Tag, Loader2, CheckCircle2, Star, Shield, AlertCircle,
  FileCheck, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Car, Bike, Truck } from 'lucide-react';
import { BrandSelect, ModelSelect } from './Vehicleselectcomponents';
import SearchableCitySelect from '@/components/Searchablecityselect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 9;
const LS_PENDING_SCHEDULE = 'x7_pending_verification_schedule';

// ─── Image guides per vehicle type ───────────────────────────────────────────
const VEHICLE_IMAGE_GUIDES = {
  car: [
    { label: "Front View",      hint: "Straight-on, good lighting",   bg: "from-blue-500 to-blue-700",    icon: "🚗", desc: "Stand 3–4 m in front, centre the car" },
    { label: "Rear View",       hint: "Show number plate clearly",    bg: "from-indigo-500 to-indigo-700",icon: "🔙", desc: "Include bumper & tail-lights" },
    { label: "Driver Side",     hint: "Full left-side profile",       bg: "from-violet-500 to-violet-700",icon: "↔️", desc: "Capture the entire side in one shot" },
    { label: "Passenger Side",  hint: "Full right-side profile",      bg: "from-purple-500 to-purple-700",icon: "↔️", desc: "Mirror of driver side" },
    { label: "Dashboard",       hint: "Show odometer & controls",     bg: "from-pink-500 to-pink-700",    icon: "🎛️", desc: "Capture the full instrument cluster" },
    { label: "Interior / Seats",hint: "Clean, well-lit cabin",        bg: "from-rose-500 to-rose-700",    icon: "💺", desc: "Show front & rear seats" },
    { label: "Engine Bay",      hint: "Open bonnet, clean shot",      bg: "from-orange-500 to-orange-700",icon: "⚙️", desc: "Buyers want to see engine condition" },
  ],
  bike: [
    { label: "Left Side",           hint: "Full profile, kickstand down", bg: "from-green-500 to-green-700",   icon: "🏍️", desc: "Capture entire bike from 2 m away" },
    { label: "Right Side",          hint: "Show exhaust & panel",         bg: "from-teal-500 to-teal-700",     icon: "🏍️", desc: "Right-side full profile" },
    { label: "Front View",          hint: "Headlight & forks",            bg: "from-cyan-500 to-cyan-700",     icon: "💡", desc: "Straight-on front shot" },
    { label: "Rear View",           hint: "Tail-light & tyre",            bg: "from-sky-500 to-sky-700",       icon: "🔚", desc: "Include number plate" },
    { label: "Instrument Cluster",  hint: "Show odometer reading",        bg: "from-blue-500 to-blue-700",     icon: "🎛️", desc: "Clearly readable km reading" },
    { label: "Engine Close-up",     hint: "Capture engine condition",     bg: "from-indigo-500 to-indigo-700", icon: "⚙️", desc: "No oil leaks visible" },
    { label: "Tyre Condition",      hint: "Both front & rear tread",      bg: "from-violet-500 to-violet-700", icon: "🔄", desc: "Show tread depth clearly" },
  ],
  ev: [
    { label: "Front View",      hint: "Show grille / fascia design",  bg: "from-emerald-500 to-emerald-700",icon: "⚡", desc: "Straight-on front shot" },
    { label: "Rear View",       hint: "Include charging port",        bg: "from-green-500 to-green-700",    icon: "🔋", desc: "Show charging port location" },
    { label: "Left Profile",    hint: "Full side shot",               bg: "from-teal-500 to-teal-700",      icon: "↔️", desc: "Complete side view" },
    { label: "Charging Port",   hint: "Type & condition",             bg: "from-cyan-500 to-cyan-700",      icon: "🔌", desc: "Close-up of port & connector" },
    { label: "Dashboard",       hint: "Infotainment & range display", bg: "from-sky-500 to-sky-700",        icon: "📱", desc: "Show battery level & range" },
    { label: "Interior",        hint: "Seats & cabin space",          bg: "from-blue-500 to-blue-700",      icon: "💺", desc: "Clean, bright cabin shot" },
    { label: "Frunk / Boot",    hint: "Show storage space",           bg: "from-indigo-500 to-indigo-700",  icon: "📦", desc: "Open frunk/boot fully" },
  ],
  commercial: [
    { label: "Front View",    hint: "Bumper, grille, headlights",  bg: "from-amber-500 to-amber-700",   icon: "🚛", desc: "Straight-on front shot" },
    { label: "Driver Side",   hint: "Full left profile",           bg: "from-orange-500 to-orange-700",  icon: "↔️", desc: "Full side from 5 m away" },
    { label: "Cargo Area",    hint: "Open body / container",       bg: "from-red-500 to-red-700",        icon: "📦", desc: "Show loading area clearly" },
    { label: "Cabin Interior",hint: "Seats & dashboard",           bg: "from-rose-500 to-rose-700",      icon: "🎛️", desc: "Include odometer reading" },
    { label: "Rear View",     hint: "Tail-gate / doors & plate",   bg: "from-pink-500 to-pink-700",      icon: "🔚", desc: "Rear access point fully visible" },
    { label: "Tyres",         hint: "All tyres tread depth",       bg: "from-fuchsia-500 to-fuchsia-700",icon: "🔄", desc: "Front & rear tyre close-ups" },
    { label: "Engine Bay",    hint: "Open bonnet",                  bg: "from-violet-500 to-violet-700",  icon: "⚙️", desc: "Show engine condition" },
  ],
};

// ─── EV subtype options shown as visual cards ─────────────────────────────────
const EV_SUBTYPE_OPTIONS = [
  { value: 'car',        label: 'EV Car',        icon: '', hint: 'Cars & sedans' },
  { value: 'bike',       label: 'EV Bike',       icon: '', hint: 'Bikes & scooters' },
  { value: 'commercial', label: 'EV Commercial', icon: '', hint: 'Trucks & vans' },
];

const isHoliday = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr + 'T00:00:00');
  if (d.getDay() === 0) return true;
  if (d.getMonth() === 4 && d.getDate() === 1) return true;
  return false;
};
const todayStr = () => new Date().toISOString().split('T')[0];
const defaultScheduleForm = (city = '', phone = '') => ({
  slot1_date: todayStr(), slot1_from: '10:00', slot1_to: '11:30',
  slot2_date: todayStr(), slot2_from: '14:00', slot2_to: '15:30',
  address_street: '', address_city: city, address_state: '',
  address_pin: '', seller_phone: phone,
});

const UploadVehicleWizard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [cities, setCities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [features, setFeatures] = useState([]);
  const [rtoCodes, setRtoCodes] = useState([]);
  const [cityQR, setCityQR] = useState(null);
  const [vehicleSegments, setVehicleSegments] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [guideSlide, setGuideSlide] = useState(0);

  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [featuredPricing, setFeaturedPricing] = useState(null);
  const [featuredSlotsAvailable, setFeaturedSlotsAvailable] = useState(true);
  const [wantFeatured, setWantFeatured] = useState(false);
  const [verificationPricing, setVerificationPricing] = useState(null);
  const [wantVerification, setWantVerification] = useState(false);
  const [submittedVehicleId, setSubmittedVehicleId] = useState(null);
  const [showVerificationScheduleStep, setShowVerificationScheduleStep] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(defaultScheduleForm());
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);

  const [regNoExists, setRegNoExists] = useState(false);
  const [regNoChecking, setRegNoChecking] = useState(false);
  const [regNoCheckTimer, setRegNoCheckTimer] = useState(null);

  const [formData, setFormData] = useState({
    vehicle_type: '', vehicle_segment: '', ev_subtype: '',
    brand_id: '', model_id: '', variant_id: '',
    manufacturing_year: '', registration_no: '', rto_code: '',
    registration_type: 'standard', city: '',
    fuel_type: '', transmission: '', ownership: 1, seater: 5,
    km_run_range: '', color: '', images: [], selectedFeatures: [],
    price: '', contact_phone: '', payment_screenshot: '',
    has_insurance: null, insurance_company: '', insurance_expiry: '',
    has_puc: null, puc_expiry: '',
    is_choice_number: null,
  });

  // ── Slideshow ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (currentStep !== 4) return;
    const vehicleKey = formData.vehicle_type === 'ev' ? 'ev' : (formData.vehicle_type || 'car');
    const guides = VEHICLE_IMAGE_GUIDES[vehicleKey] || VEHICLE_IMAGE_GUIDES.car;
    const timer = setInterval(() => setGuideSlide(prev => (prev + 1) % guides.length), 1500);
    return () => clearInterval(timer);
  }, [currentStep, formData.vehicle_type]);
  useEffect(() => { setGuideSlide(0); }, [formData.vehicle_type]);

  // ── Restore pending schedule ───────────────────────────────────────────────
  useEffect(() => {
    const pending = localStorage.getItem(LS_PENDING_SCHEDULE);
    if (pending) {
      try {
        const parsed = JSON.parse(pending);
        setSubmittedVehicleId(parsed.vehicleId);
        setScheduleForm(parsed.scheduleForm || defaultScheduleForm());
        setShowVerificationScheduleStep(true);
        toast.info('You have an incomplete verification schedule.', { duration: 6000 });
      } catch { localStorage.removeItem(LS_PENDING_SCHEDULE); }
    }
  }, []);

  useEffect(() => { fetchInitialData(); }, []);

  // Re-fetch models whenever brand OR ev_subtype changes
  useEffect(() => {
    if (formData.brand_id) fetchModels(formData.brand_id);
    else setModels([]);
  }, [formData.brand_id, formData.ev_subtype]);

  useEffect(() => { if (formData.model_id) fetchVariants(formData.model_id); }, [formData.model_id]);
  useEffect(() => {
    if (formData.vehicle_type) {
      fetchFeatures(formData.vehicle_type);
      fetchVehicleSegments(formData.vehicle_type);
    }
  }, [formData.vehicle_type]);
  useEffect(() => {
    if (formData.city) {
      fetchCityQR(formData.city);
      fetchFeaturedPricing(formData.city, formData.vehicle_type);
      fetchVerificationPricing(formData.city);
    }
  }, [formData.city, formData.vehicle_type]);
  useEffect(() => {
    if (!showVerificationScheduleStep) {
      setScheduleForm(prev => ({
        ...prev,
        address_city: formData.city || prev.address_city,
        seller_phone: formData.contact_phone || prev.seller_phone,
      }));
    }
  }, [formData.city, formData.contact_phone, showVerificationScheduleStep]);

  const fetchInitialData = async () => {
    try {
      const [citiesRes, brandsRes, rtoRes, insuranceRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/brands`),
        axios.get(`${API}/rto-codes`),
        axios.get(`${API}/insurance-companies`),
      ]);
      setCities(citiesRes.data.cities);
      setBrands(brandsRes.data.brands);
      setRtoCodes(rtoRes.data.rto_codes);
      setInsuranceCompanies(insuranceRes.data.companies || []);
    } catch { toast.error('Failed to load form data'); }
  };

  const checkRegistrationExists = (rtoCode, regNo, regType) => {
  if (regNoCheckTimer) clearTimeout(regNoCheckTimer);

  // Reset state when input is cleared
  if (!regNo || regNo.trim().length === 0) {
    setRegNoExists(false);
    setRegNoChecking(false);
    return;
  }

  // Build the full registration number exactly as it would be stored
  const fullReg = regType === 'bh'
    ? regNo.trim().toUpperCase()
    : `${rtoCode} ${regNo}`.trim().toUpperCase();

  // Need at least RTO code + some number to be worth checking
  if (fullReg.replace(/\s/g, '').length < 6) {
    setRegNoExists(false);
    return;
  }

  setRegNoChecking(true);

  const timer = setTimeout(async () => {
    try {
      // Search without vehicle_status filter so ALL statuses are checked
      // Use the full registration including spaces for accurate matching
      const res = await axios.get(`${API}/vehicles`, {
        params: {
          registration_number: fullReg,
          limit: 1,
          skip: 0,
          // Deliberately NOT passing vehicle_status so backend checks all
        }
      });
      const found = (res.data.total || 0) > 0;
      setRegNoExists(found);
    } catch {
      // On API error, don't block the user — assume available
      setRegNoExists(false);
    } finally {
      setRegNoChecking(false);
    }
  }, 700);

  setRegNoCheckTimer(timer);
};

  // Pass ev_subtype to backend so only models for that EV subtype are returned
  const fetchModels = async (brandId) => {
    try {
      const params = new URLSearchParams({ brand_id: brandId });
      if (formData.vehicle_type === 'ev' && formData.ev_subtype) {
        params.append('ev_subtype', formData.ev_subtype);
      }
      const res = await axios.get(`${API}/models?${params}`);
      setModels(res.data.models);
    } catch { setModels([]); }
  };

  const fetchVariants        = async (modelId)    => { try { const r = await axios.get(`${API}/variants?model_id=${modelId}`); setVariants(r.data.variants); } catch {} };
  const fetchFeatures        = async (vtype)       => { try { const r = await axios.get(`${API}/features?vehicle_type=${vtype}`); setFeatures(r.data.features); } catch {} };
  const fetchVehicleSegments = async (vtype)       => { try { const r = await axios.get(`${API}/vehicle-segments?vehicle_type=${vtype}`); setVehicleSegments(r.data.segments || []); } catch {} };
  const fetchCityQR          = async (cityName)    => { try { const r = await axios.get(`${API}/cities/${cityName}/qr`); setCityQR(r.data); } catch {} };
  const fetchFeaturedPricing = async (city, vtype) => {
    try {
      const vt = vtype || formData.vehicle_type;
      const url = vt ? `${API}/featured-pricing/${city}?vehicle_type=${vt}` : `${API}/featured-pricing/${city}`;
      const r = await axios.get(url);
      setFeaturedPricing(r.data);
      setFeaturedSlotsAvailable(r.data.available !== false);
    } catch { setFeaturedPricing(null); }
  };
  const fetchVerificationPricing = async (city) => {
    try { const r = await axios.get(`${API}/verification-pricing/${city}`); setVerificationPricing(r.data); }
    catch { setVerificationPricing(null); }
  };

  const basePrice = cityQR?.upload_price || 0;
  const getPriceBreakdown = () => {
    let base = basePrice;
    let featuredAdd = wantFeatured && featuredPricing?.price ? featuredPricing.price : 0;
    let verificationAdd = wantVerification && verificationPricing?.price ? verificationPricing.price : 0;
    let subtotal = base + featuredAdd + verificationAdd;
    let discount = 0;
    if (appliedCoupon) {
      discount = appliedCoupon.discount_amount;
      subtotal = appliedCoupon.discounted_price + featuredAdd + verificationAdd;
    }
    const gst = Math.round(subtotal * 0.18 * 100) / 100;
    return { base, featuredAdd, verificationAdd, discount, subtotal, gst, total: Math.round((subtotal + gst) * 100) / 100 };
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) { toast.error('Enter coupon code'); return; }
    if (!formData.city) { toast.error('Select city first'); return; }
    setCouponLoading(true);
    try {
      const r = await axios.post(`${API}/coupons/validate`,
        { code: couponInput.trim().toUpperCase(), city: formData.city, base_price: basePrice },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppliedCoupon(r.data); toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid coupon'); setAppliedCoupon(null); }
    finally { setCouponLoading(false); }
  };
  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(''); toast.info('Coupon removed'); };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 7) { toast.error('Maximum 7 images'); return; }
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setFormData(prev => ({ ...prev, images: [...prev.images, reader.result] }));
      reader.readAsDataURL(file);
    });
  };
  const removeImage = (i) => setFormData({ ...formData, images: formData.images.filter((_, idx) => idx !== i) });
  const formatPrice = (v) => { const n = v.replace(/\D/g, ''); return n ? new Intl.NumberFormat('en-IN').format(n) : ''; };
  const formatToIndianUnit = (v) => {
    const num = Number(v.replace(/,/g, ''));
    if (!num) return '';
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Crore`;
    if (num >= 100000)   return `${(num / 100000).toFixed(2)} Lakh`;
    return `${num}`;
  };

  // Whether to show seater field: regular car, OR EV car subtype
  const showSeater = formData.vehicle_type === 'car' || (formData.vehicle_type === 'ev' && formData.ev_subtype === 'car');

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.vehicle_type || !formData.brand_id || !formData.model_id || !formData.manufacturing_year || !formData.city) {
          toast.error('Please fill all basic information'); return false;
        }
        if (formData.vehicle_type === 'ev' && !formData.ev_subtype) {
          toast.error('Please select EV Type (Car, Bike or Commercial)'); return false;
        }
        break;
      case 2:
        if (!formData.fuel_type || !formData.transmission || !formData.km_run_range || !formData.color) {
          toast.error('Please fill all specifications'); return false;
        }
        break;
      case 3:
        if (formData.has_insurance === null) { toast.error('Please select insurance status'); return false; }
        if (formData.has_insurance === true) {
          if (!formData.insurance_company) { toast.error('Please select insurance company'); return false; }
          if (!formData.insurance_expiry)  { toast.error('Please select insurance expiry date'); return false; }
        }
        if (formData.has_puc === null) { toast.error('Please select PUC status'); return false; }
        if (formData.has_puc === true) {
          if (!formData.puc_expiry) { toast.error('Please select PUC expiry date'); return false; }
        }
        break;
      case 4:
        if (formData.images.length === 0) { toast.error('Upload at least 1 image'); return false; }
        break;
      case 6:
        if (!formData.price || !formData.contact_phone || !formData.registration_no) {
          toast.error('Please provide all required details'); return false;
        }
        if (formData.contact_phone.replace(/\D/g, '').length !== 10) {
          toast.error('Phone must be 10 digits'); return false;
        }
        if (formData.registration_type === 'standard' && !formData.rto_code) {
          toast.error('Select RTO code'); return false;
        }
        // ── NEW CHECK ──
        if (regNoExists) {
          toast.error('This registration number is already listed. Please check your number.');
          return false;
        }
        if (regNoChecking) {
          toast.error('Please wait, verifying registration number...');
          return false;
        }
        break;
      default: break;
    }
    return true;
  };

  const handleNext = () => { if (validateStep()) setCurrentStep(s => s + 1); };
  const handleBack = () => setCurrentStep(s => s - 1);

  const handleSubmitAfterPayment = async (paymentData) => {
  setLoading(true);
  try {
    const fullRegNo = formData.registration_type === 'bh'
      ? formData.registration_no.toUpperCase()
      : `${formData.rto_code} ${formData.registration_no}`.toUpperCase();
 
    // ── STEP 1: Create vehicle ─────────────────────────────────────────────────
    const vehicleData = {
      registration_no:    fullRegNo,
      vehicle_type:       formData.vehicle_type,
      vehicle_segment:    formData.vehicle_segment || '',
      ev_subtype:         formData.vehicle_type === 'ev' ? formData.ev_subtype : null,
      brand_id:           formData.brand_id,
      model_id:           formData.model_id,
      variant_id:         formData.variant_id,
      transmission:       formData.transmission,
      fuel_type:          formData.vehicle_type === 'ev' ? 'Electric' : formData.fuel_type,
      ownership:          parseInt(formData.ownership) || 1,
      seater:             showSeater ? (parseInt(formData.seater) || 5) : null,
      km_run_range:       formData.km_run_range,
      color:              formData.color,
      images:             formData.images,
      features:           formData.selectedFeatures || [],
      price:              formData.price,
      contact_phone:      formData.contact_phone,
      city:               formData.city,
      manufacturing_year: formData.manufacturing_year,
      has_insurance:      formData.has_insurance,
      insurance_company:  formData.has_insurance ? formData.insurance_company : null,
      insurance_expiry:   formData.has_insurance ? formData.insurance_expiry  : null,
      has_puc:            formData.has_puc,
      puc_expiry:         formData.has_puc ? formData.puc_expiry : null,
      is_choice_number:   formData.is_choice_number === true,
    };
 
    let vehicleId;
    try {
      const vehicleRes = await axios.post(
        `${API}/vehicles`,
        vehicleData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      vehicleId = vehicleRes.data.vehicle_id;
      if (!vehicleId) throw new Error('No vehicle ID returned');
      setSubmittedVehicleId(vehicleId);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to create vehicle';
      console.error('[Step 1 - Create vehicle]', err);
      // Payment already captured — tell user to contact support with payment ID
      toast.error(
        `Vehicle listing failed: ${msg}. Your payment (${paymentData.razorpay_payment_id}) was captured — please contact support.`,
        { duration: 10000 }
      );
      return; // stop here, don't proceed
    }
 
    // ── STEP 2: Add featured (optional, non-blocking) ─────────────────────────
    if (wantFeatured && featuredSlotsAvailable) {
      try {
        await axios.post(
          `${API}/superadmin/featured-vehicles`,
          { vehicle_id: vehicleId, city: formData.city },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {
        console.warn('[Step 2 - Featured] Failed (non-critical):', e);
        // Don't block — featured failing should not stop the submission
      }
    }
 
    // ── STEP 3: Record payment ────────────────────────────────────────────────
    const breakdown   = getPriceBreakdown();
    const couponCode  = appliedCoupon?.code ? String(appliedCoupon.code).toUpperCase().trim() : null;
    const discountAmt = appliedCoupon?.discount_amount ? Number(appliedCoupon.discount_amount) : 0;
 
    try {
      await axios.post(
        `${API}/payments`,
        {
          vehicle_id:             vehicleId,
          registration_no:        fullRegNo,
          type:                   'vehicle_upload',
          razorpay_payment_id:    paymentData.razorpay_payment_id,
          razorpay_order_id:      paymentData.razorpay_order_id,
          amount:                 Number(Math.round(breakdown.total)),
          coupon_code:            couponCode,
          discount_amount:        isNaN(discountAmt) ? 0 : discountAmt,
          featured:               wantFeatured,
          featured_amount:        wantFeatured ? (featuredPricing?.price || 0) : 0,
          verification_requested: wantVerification,
          verification_amount:    wantVerification ? (verificationPricing?.price || 0) : 0,
        },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to record payment';
      console.error('[Step 3 - Record payment]', err);
      // Vehicle was created but payment record failed — still navigate,
      // backend invoice email may still fire; log for reconciliation
      toast.warning(
        `Vehicle listed but payment record failed: ${msg}. Contact support with payment ID: ${paymentData.razorpay_payment_id}`,
        { duration: 10000 }
      );
      // Still navigate — vehicle exists, don't leave user stuck
      navigate('/dashboard');
      return;
    }
 
    // ── STEP 4: All done ──────────────────────────────────────────────────────
    toast.success('Vehicle submitted successfully! Invoice sent to your email.');
 
    if (wantVerification) {
      const init = defaultScheduleForm(formData.city, formData.contact_phone);
      setScheduleForm(init);
      localStorage.setItem(
        LS_PENDING_SCHEDULE,
        JSON.stringify({ vehicleId, scheduleForm: init })
      );
      setShowVerificationScheduleStep(true);
    } else {
      navigate('/dashboard');
    }
 
  } catch (error) {
    // Unexpected / unhandled error
    const msg = error.response?.data?.detail || error.message || 'Submission failed';
    console.error('[handleSubmitAfterPayment] Unexpected error:', error);
    toast.error(`${msg}. If payment was deducted, contact support with payment ID: ${paymentData.razorpay_payment_id || ''}`, { duration: 10000 });
  } finally {
    setLoading(false);
  }
};
 
 
const handleRazorpayPayment = async () => {
  if (!validateStep()) return;
 
  let orderData;
  try {
    const breakdown = getPriceBreakdown();
    const { data } = await axios.post(
      `${API}/create-order`,
      { amount: Math.round(breakdown.total) }
    );
    orderData = data;
  } catch (err) {
    const msg = err.response?.data?.detail || err.message || 'Could not create payment order';
    console.error('[handleRazorpayPayment - create-order]', err);
    toast.error(msg);
    return;
  }
 
  const options = {
    key:         process.env.REACT_APP_RAZORPAY_KEY,
    amount:      orderData.amount,
    currency:    'INR',
    name:        'X7 Autoz',
    description: 'Vehicle Listing Fee',
    order_id:    orderData.order_id,
 
    handler: async (response) => {
      // Verify signature first
      let verified = false;
      try {
        const verifyRes = await axios.post(`${API}/verify-payment`, {
          razorpay_order_id:   response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature:  response.razorpay_signature,
        });
        verified = verifyRes.data.status === 'success';
      } catch (err) {
        console.error('[handleRazorpayPayment - verify-payment]', err);
        toast.error('Payment verification failed. Please contact support.');
        return;
      }
 
      if (!verified) {
        toast.error('Payment verification failed. Please contact support.');
        return;
      }
 
      toast.success('Payment successful! Submitting your listing...');
      await handleSubmitAfterPayment(response);
    },
 
    prefill: { contact: formData.contact_phone },
    theme:   { color: '#f97316' },
    modal: {
      ondismiss: () => toast.info('Payment cancelled.'),
    },
  };
 
  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', (response) => {
    const msg = response.error?.description || 'Payment failed. Please try again.';
    console.error('[Razorpay payment.failed]', response.error);
    toast.error(msg);
  });
  rzp.open();
};

  const handleSubmitSchedule = async () => {
    if (!scheduleForm.address_street.trim()) { toast.error('Please enter street address'); return; }
    if (!scheduleForm.address_pin.trim() || scheduleForm.address_pin.length < 6) { toast.error('Please enter a valid 6-digit PIN code'); return; }
    if (!scheduleForm.seller_phone || scheduleForm.seller_phone.replace(/\D/g, '').length !== 10) { toast.error('Please enter a valid 10-digit phone number'); return; }
    if (isHoliday(scheduleForm.slot1_date)) { toast.error('Slot 1 date is a holiday. Please choose another date.'); return; }
    if (isHoliday(scheduleForm.slot2_date)) { toast.error('Slot 2 date is a holiday. Please choose another date.'); return; }
    if (!submittedVehicleId) { toast.error('Vehicle ID is missing. Please contact support.'); return; }
    localStorage.setItem(LS_PENDING_SCHEDULE, JSON.stringify({ vehicleId: submittedVehicleId, scheduleForm }));
    setScheduleSubmitting(true);
    try {
      await axios.post(`${API}/verification/schedule`, {
        vehicle_id: submittedVehicleId,
        availability_slots: [
          { date: scheduleForm.slot1_date, from_time: scheduleForm.slot1_from, to_time: scheduleForm.slot1_to },
          { date: scheduleForm.slot2_date, from_time: scheduleForm.slot2_from, to_time: scheduleForm.slot2_to },
        ],
        address_street: scheduleForm.address_street,
        address_city:   scheduleForm.address_city,
        address_state:  scheduleForm.address_state,
        address_pin:    scheduleForm.address_pin,
        seller_phone:   scheduleForm.seller_phone,
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem(LS_PENDING_SCHEDULE);
      toast.success('Verification schedule submitted! A partner will be assigned shortly.');
      setShowVerificationScheduleStep(false);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit schedule. Please try again or contact support.');
    } finally { setScheduleSubmitting(false); }
  };

  const vehicleTypes = [
    { key: 'car',        label: 'CAR',        icon: <Car size={18} />  },
    { key: 'bike',       label: 'BIKE',       icon: <Bike size={18} /> },
    { key: 'ev',         label: 'ELECTRIC',   icon: <Zap size={18} />  },
    { key: 'commercial', label: 'COMMERCIAL', icon: <Truck size={18} />},
  ];
  const fuelTypes     = formData.vehicle_type === 'bike' ? ['Petrol', 'CNG'] : ['Petrol', 'Diesel', 'CNG', 'Hybrid'];
  const transmissions = formData.vehicle_type === 'bike' ? ['Automatic'] : ['Manual', 'Automatic', 'AMT', 'CVT', 'DCT', 'IMT'];
  const colors   = ['Yellow & Golden', 'White', 'Beige, Bronze & Brown', 'Maroon & Red', 'Silver', 'Gray & Black', 'Blue', 'Green & Orange', 'Violet, Purple & Wine'];
  const kmRanges = ['0-5,000', '5,000-10,000', '10,000-20,000', '20,000-40,000', '40,000-50,000', '50,000-65,000', '65,000-85,000', '85,000-100,000', '100,000+'];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 26 }, (_, i) => currentYear - i);
  const stepTitles = ['Basic Information', 'Specifications', 'Insurance & PUC', 'Upload Images', 'Features (Optional)', 'Price & Contact', 'Verify Vehicle', 'Feature Your Vehicle', 'Payment'];
  const progress = (currentStep / TOTAL_STEPS) * 100;

  const YesNoToggle = ({ value, onChange, label }) => (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-3">{label}</p>
      <div className="flex gap-3">
        <button type="button" onClick={() => onChange(true)} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all border-2 flex items-center justify-center gap-2 ${value === true ? 'bg-green-500 border-green-500 text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-green-300 hover:bg-green-50'}`}><CheckCircle2 className="h-4 w-4" /> Yes</button>
        <button type="button" onClick={() => onChange(false)} className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all border-2 flex items-center justify-center gap-2 ${value === false ? 'bg-red-500 border-red-500 text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-red-300 hover:bg-red-50'}`}><X className="h-4 w-4" /> No</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header cities={cities} selectedCity={formData.city} setSelectedCity={(city) => setFormData({ ...formData, city })} />
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-secondary-800 mb-2">Sell Your Vehicle</h1>
            <p className="text-secondary-600">Step {currentStep} of {TOTAL_STEPS}</p>
            <Progress value={progress} className="mt-4" />
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <Card>
                <CardHeader><CardTitle>{stepTitles[currentStep - 1]}</CardTitle></CardHeader>
                <CardContent className="space-y-6">

                  {/* ══════════════════════════════════════════════════
                      STEP 1 – BASIC INFORMATION
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 1 && (
                    <div className="grid md:grid-cols-2 gap-8">

                      {/* Advisory banner */}
                      <div className="md:col-span-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                        <p className="text-xs text-amber-700">Please provide <span className="font-semibold">accurate and complete information</span> — listings with correct details attract more buyers and sell faster.</p>
                      </div>

                      {/* Left panel */}
                      <div className="bg-gray-50 p-6 rounded-xl">
                        <p className="text-xs text-orange-500 font-semibold mb-2">STEP 01 OF {TOTAL_STEPS}</p>
                        <h1 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">Basic <br /> Information</h1>
                        <p className="text-gray-600 text-sm mb-6">Core specifications of your vehicle.</p>
                        <div className="bg-white p-4 rounded-xl border shadow-sm">
                          <p className="text-sm font-semibold mb-1">⭐ EDITORIAL STANDARDS</p>
                          <p className="text-xs text-gray-500">Our curators use these details to match your vehicle with high-intent buyers.</p>
                        </div>
                      </div>

                      {/* Right panel – all fields */}
                      <div className="space-y-6">

                        {/* Vehicle type tiles */}
                        <div>
                          <p className="text-sm text-gray-500 mb-2">SELECT VEHICLE TYPE</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {vehicleTypes.map(item => (
                              <div key={item.key}
                                onClick={() => setFormData({
                                  ...formData,
                                  vehicle_type: item.key,
                                  ev_subtype: '',
                                  brand_id: '', model_id: '', variant_id: '',
                                  fuel_type: item.key === 'ev' ? 'Electric' : '',
                                })}
                                className={`cursor-pointer rounded-xl border p-3 flex flex-col items-center gap-2 text-center transition-all ${formData.vehicle_type === item.key ? 'border-orange-500 bg-white text-orange-600' : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                                {item.icon}
                                <p className="text-[10px] sm:text-xs font-semibold">{item.label}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ─────────────────────────────────────────────────────
                            EV SUBTYPE SELECTOR
                            Shown only when vehicle_type === 'ev'.
                            Visual card grid — selecting resets brand/model/variant.
                            BrandSelect below reads formData.vehicle_type ('ev') AND
                            formData.ev_subtype ('car'|'bike'|'commercial') separately,
                            so it can filter brand.category==='ev' AND
                            brand.ev_subtypes.includes(ev_subtype).
                        ───────────────────────────────────────────────────── */}
                        {formData.vehicle_type === 'ev' && (
                          <div>
                            <p className="text-sm text-gray-700 font-medium mb-2">EV TYPE *</p>
                            <div className="grid grid-cols-3 gap-2">
                              {EV_SUBTYPE_OPTIONS.map(sub => {
                                const selected = formData.ev_subtype === sub.value;
                                return (
                                  <button key={sub.value} type="button"
                                    onClick={() => setFormData({
                                      ...formData,
                                      ev_subtype: sub.value,
                                      brand_id: '', model_id: '', variant_id: '',
                                    })}
                                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all ${selected ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-sm' : 'border-gray-200 bg-gray-50 text-gray-500 hover:border-orange-300 hover:bg-orange-50/50'}`}>
                                    <span className="text-xl leading-none">{sub.icon}</span>
                                    <span className="text-xs font-semibold leading-tight">{sub.label}</span>
                                    <span className="text-[10px] text-gray-400 leading-tight">{sub.hint}</span>
                                    {selected && <span className="text-[10px] bg-orange-500 text-white px-1.5 py-0.5 rounded font-bold">✓ Selected</span>}
                                  </button>
                                );
                              })}
                            </div>
                            {formData.ev_subtype ? (
                              <p className="text-xs text-blue-500 mt-2 flex items-center gap-1">
                                💡 Showing only <strong>{EV_SUBTYPE_OPTIONS.find(s => s.value === formData.ev_subtype)?.label}</strong> brands &amp; models.
                              </p>
                            ) : (
                              <p className="text-xs text-amber-600 mt-2">⚠️ Select EV type above to load matching brands.</p>
                            )}
                          </div>
                        )}

                        {/* Vehicle segment */}
                        {formData.vehicle_type && vehicleSegments.length > 0 && (
                          <div>
                            <p className="text-sm text-gray-700 mb-2">VEHICLE SEGMENT</p>
                            <div className="flex flex-wrap gap-2">
                              {vehicleSegments.map(seg => (
                                <button key={seg} onClick={() => setFormData({ ...formData, vehicle_segment: seg })}
                                  className={`px-4 py-1.5 rounded text-sm transition-all ${formData.vehicle_segment === seg ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>
                                  {seg}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Brand + Model */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">BRAND</p>
                            {/*
                              ════════════════════════════════════════════════
                              ROOT FIX — pass vehicleType and evSubtype
                              separately so BrandSelect can correctly filter:

                              • vehicleType = formData.vehicle_type  ('ev')
                                → BrandSelect checks brand.category === 'ev'
                              • evSubtype   = formData.ev_subtype    ('bike')
                                → BrandSelect additionally checks
                                  brand.ev_subtypes.includes('bike')
                                  (with legacy sub_category fallback)

                              Previously `effectiveBrandCategory` was passed as
                              vehicleType — it equalled formData.ev_subtype
                              (e.g. 'bike'), causing BrandSelect to look for
                              brand.category === 'bike' and find nothing.
                              ════════════════════════════════════════════════
                            */}
                            <BrandSelect
                              brands={brands}
                              vehicleType={formData.vehicle_type}
                              evSubtype={formData.ev_subtype}
                              value={formData.brand_id}
                              onChange={(id) => setFormData({ ...formData, brand_id: id, model_id: '', variant_id: '' })}
                            />
                            {formData.vehicle_type === 'ev' && !formData.ev_subtype && (
                              <p className="text-xs text-amber-500 mt-1">Select EV Type first to see brands.</p>
                            )}
                          </div>

                          <div>
                            <p className="text-xs text-gray-500 mb-1">MODEL</p>
                            {/*
                              ModelSelect receives evSubtype so the API call
                              GET /models?brand_id=...&ev_subtype=bike returns
                              only models tagged for that EV subtype.
                            */}
                            <ModelSelect
                              brandId={formData.brand_id}
                              evSubtype={formData.vehicle_type === 'ev' ? formData.ev_subtype : undefined}
                              value={formData.model_id}
                              onChange={(id) => setFormData({ ...formData, model_id: id, variant_id: '' })}
                            />
                          </div>
                        </div>

                        {/* Variant + Year */}
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">VARIANT <span className="text-gray-400 font-normal normal-case text-[10px]">(optional)</span></p>
                            <Select value={formData.variant_id} onValueChange={(v) => setFormData({ ...formData, variant_id: v })}>
                              <SelectTrigger><SelectValue placeholder="Not Interested" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="not_interested"><span className="text-gray-400 italic">Not Interested</span></SelectItem>
                                {variants.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <p className="text-[11px] text-blue-500 mt-1.5 flex items-center gap-1">💡 Please select your vehicle variant to attract more buyers.</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">MANUFACTURING YEAR</p>
                            <Select value={formData.manufacturing_year} onValueChange={(v) => setFormData({ ...formData, manufacturing_year: v })}>
                              <SelectTrigger><SelectValue placeholder="Select year" /></SelectTrigger>
                              <SelectContent>{years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* City */}
                        <div className='w-full'>
                          <p className="text-xs text-gray-500 mb-1">CITY</p>
                          <SearchableCitySelect
                            cities={cities}
                            value={formData.city}
                            onValueChange={(v) => setFormData({ ...formData, city: v })}
                            placeholder="Select city"
                            contentWidth="100%"
                            triggerClassName="w-full rounded-lg border-gray-200"
                          />
                        </div>

                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 2 – SPECIFICATIONS
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 2 && (
                    <div className="space-y-8">
                      <div>
                        <h2 className="text-lg font-semibold mb-4 border-l-4 border-orange-500 pl-3 uppercase tracking-wide">Power & Drive</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Fuel Type</p>
                            {formData.vehicle_type === 'ev' ? (
                              <div className="inline-block px-4 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium">Electric</div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {fuelTypes.map(f => <button key={f} onClick={() => setFormData({ ...formData, fuel_type: f })} className={`px-4 py-2 rounded-lg text-sm transition-all ${formData.fuel_type === f ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>{f}</button>)}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Transmission</p>
                            <div className="flex flex-wrap gap-2">
                              {(formData.vehicle_type === 'ev' ? ['Automatic'] : transmissions).map(t => <button key={t} onClick={() => setFormData({ ...formData, transmission: t })} className={`px-4 py-2 rounded-lg text-sm transition-all ${formData.transmission === t ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>{t}</button>)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold mb-4 border-l-4 border-orange-500 pl-3 uppercase tracking-wide">Usage & Capacity</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Ownership</p>
                            <Select value={formData.ownership?.toString()} onValueChange={(v) => setFormData({ ...formData, ownership: v })}>
                              <SelectTrigger><SelectValue placeholder="Select owner" /></SelectTrigger>
                              <SelectContent>{[1,2,3,4,5,6,7].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          {/* Seater for car OR ev+car */}
                          {showSeater && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Seater</p>
                              <Select value={formData.seater?.toString()} onValueChange={(v) => setFormData({ ...formData, seater: v })}>
                                <SelectTrigger><SelectValue placeholder="Select seater" /></SelectTrigger>
                                <SelectContent>{[2,4,5,6,7,8].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          )}
                          <div>
                            <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Kilometers Run</p>
                            <Select value={formData.km_run_range} onValueChange={(v) => setFormData({ ...formData, km_run_range: v })}>
                              <SelectTrigger><SelectValue placeholder="Select KM range" /></SelectTrigger>
                              <SelectContent>{kmRanges.map(r => <SelectItem key={r} value={r}>{r} KM</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Color</p>
                        <Select value={formData.color} onValueChange={(v) => setFormData({ ...formData, color: v })}>
                          <SelectTrigger><SelectValue placeholder="Select color" /></SelectTrigger>
                          <SelectContent>{colors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 3 – INSURANCE & PUC
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 3 && (
                    <div className="grid md:grid-cols-2 gap-8">
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                        <p className="text-xs text-blue-500 font-semibold mb-2">STEP 03 OF {TOTAL_STEPS}</p>
                        <h2 className="text-2xl font-bold text-gray-900 mb-3 leading-tight">Insurance<br />& PUC</h2>
                        <p className="text-gray-600 text-sm mb-5">Buyers check insurance and PUC status before making purchase decisions. Providing accurate details builds trust.</p>
                        <div className="space-y-3">
                          <div className="bg-white rounded-xl p-3 border border-blue-100 flex items-start gap-3"><div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"><Shield className="h-4 w-4 text-blue-600" /></div><div><p className="text-xs font-semibold text-gray-700">Valid Insurance</p><p className="text-xs text-gray-500 mt-0.5">Boosts buyer confidence and listing credibility</p></div></div>
                          <div className="bg-white rounded-xl p-3 border border-green-100 flex items-start gap-3"><div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0"><FileCheck className="h-4 w-4 text-green-600" /></div><div><p className="text-xs font-semibold text-gray-700">Valid PUC Certificate</p><p className="text-xs text-gray-500 mt-0.5">Mandatory for vehicle transfer in most states</p></div></div>
                        </div>
                      </div>
                      <div className="space-y-8">
                        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
                          <div className="flex items-center gap-2 pb-3 border-b border-gray-100"><Shield className="h-5 w-5 text-blue-500" /><h3 className="font-semibold text-gray-800">Vehicle Insurance</h3></div>
                          <YesNoToggle label="Does this vehicle have active insurance?" value={formData.has_insurance} onChange={(v) => setFormData({ ...formData, has_insurance: v, insurance_company: '', insurance_expiry: '' })} />
                          <AnimatePresence>
                            {formData.has_insurance === true && (
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }} className="space-y-4 overflow-hidden">
                                <div>
                                  <Label className="text-xs text-gray-500 uppercase mb-1 block">Insurance Company *</Label>
                                  <Select value={formData.insurance_company} onValueChange={(v) => setFormData({ ...formData, insurance_company: v })}>
                                    <SelectTrigger className="h-11"><SelectValue placeholder="Select insurance company" /></SelectTrigger>
                                    <SelectContent>{insuranceCompanies.length > 0 ? insuranceCompanies.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>) : <SelectItem value="_none" disabled>No companies available</SelectItem>}</SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500 uppercase mb-1 block"><span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Insurance Expiry Date *</span></Label>
                                  <input type="date" value={formData.insurance_expiry} min={todayStr()} onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })} className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent" />
                                  {formData.insurance_expiry && <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Valid until {new Date(formData.insurance_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                                </div>
                              </motion.div>
                            )}
                            {formData.has_insurance === false && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"><p className="text-xs text-amber-700">⚠️ Vehicles without insurance may attract fewer buyers. Consider renewing before listing.</p></motion.div>}
                          </AnimatePresence>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
                          <div className="flex items-center gap-2 pb-3 border-b border-gray-100"><FileCheck className="h-5 w-5 text-green-500" /><h3 className="font-semibold text-gray-800">PUC Certificate</h3><span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Pollution Under Control</span></div>
                          <YesNoToggle label="Does this vehicle have a valid PUC certificate?" value={formData.has_puc} onChange={(v) => setFormData({ ...formData, has_puc: v, puc_expiry: '' })} />
                          <AnimatePresence>
                            {formData.has_puc === true && (
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                                <Label className="text-xs text-gray-500 uppercase mb-1 block"><span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> PUC Expiry Date *</span></Label>
                                <input type="date" value={formData.puc_expiry} min={todayStr()} onChange={(e) => setFormData({ ...formData, puc_expiry: e.target.value })} className="w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent" />
                                {formData.puc_expiry && <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Valid until {new Date(formData.puc_expiry).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                              </motion.div>
                            )}
                            {formData.has_puc === false && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-50 border border-red-200 rounded-xl px-4 py-3"><p className="text-xs text-red-700">⚠️ PUC is mandatory in most states. The buyer may need to obtain a new PUC before re-registration.</p></motion.div>}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 4 – IMAGES
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 4 && (() => {
                    const vehicleKey = formData.vehicle_type === 'ev' ? 'ev' : (formData.vehicle_type || 'car');
                    const guides = VEHICLE_IMAGE_GUIDES[vehicleKey] || VEHICLE_IMAGE_GUIDES.car;
                    const currentGuide = guides[guideSlide];
                    return (
                      <div className="flex flex-col lg:flex-row gap-6">
                        <div className="lg:w-60 xl:w-64 flex-shrink-0">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5"><span>📸</span> Photo Guide</p>
                          <AnimatePresence mode="wait">
                            <motion.div key={guideSlide} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }} className={`relative rounded-2xl bg-gradient-to-br ${currentGuide.bg} p-5 text-white overflow-hidden`} style={{ minHeight: 148 }}>
                              <div className="absolute -top-4 -right-4 w-20 h-20 bg-white/10 rounded-full" /><div className="absolute -bottom-5 -left-3 w-14 h-14 bg-white/10 rounded-full" />
                              <div className="relative z-10"><span className="text-4xl">{currentGuide.icon}</span><h3 className="font-bold text-base mt-2 leading-tight">{currentGuide.label}</h3><p className="text-xs text-white/80 mt-1">{currentGuide.hint}</p><p className="text-[11px] text-white/60 mt-1.5 italic">{currentGuide.desc}</p></div>
                              <div className="absolute bottom-2.5 right-3 text-[10px] text-white/60 font-medium">{guideSlide + 1} / {guides.length}</div>
                            </motion.div>
                          </AnimatePresence>
                          <div className="flex justify-center gap-1.5 mt-3">{guides.map((_, i) => <button key={i} onClick={() => setGuideSlide(i)} className={`rounded-full transition-all duration-300 ${i === guideSlide ? 'w-4 h-2 bg-orange-500' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'}`} />)}</div>
                          <div className="mt-4 space-y-1">{guides.map((g, i) => <button key={i} onClick={() => setGuideSlide(i)} className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${i === guideSlide ? 'bg-orange-50 text-orange-700 border border-orange-200' : 'text-gray-500 hover:bg-gray-50'}`}><span className="text-sm">{g.icon}</span><span className="flex-1 text-left">{g.label}</span>{i < formData.images.length ? <span className="text-green-500 font-bold">✓</span> : <span className="text-gray-300 text-[10px]">{i + 1}</span>}</button>)}</div>
                          <p className="text-[10px] text-gray-400 mt-3 text-center leading-relaxed">Up to 7 images · max 5 MB each<br />Good photos = faster sale!</p>
                        </div>
                        <div className="flex-1 space-y-4">
                          <label htmlFor="image-upload" className="block cursor-pointer">
                            <div className="border-2 border-dashed border-secondary-300 rounded-xl p-7 text-center hover:border-orange-400 hover:bg-orange-50/30 transition-colors"><Upload className="h-10 w-10 text-secondary-400 mx-auto mb-3" /><p className="text-secondary-700 font-medium mb-1">Upload your vehicle photos</p><p className="text-xs text-gray-400 mb-3">Follow the guide on the left for the best results</p><span className="inline-block px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Choose Images ({formData.images.length}/7)</span></div>
                            <Input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" id="image-upload" />
                          </label>
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {formData.images.map((img, idx) => (
                              <div key={idx} className="relative group"><img src={img} alt={guides[idx]?.label || `Photo ${idx + 1}`} className="w-full h-24 object-cover rounded-xl border-2 border-transparent group-hover:border-orange-300 transition" />{guides[idx] && <div className="absolute bottom-0 left-0 right-0 bg-black/50 rounded-b-xl px-1 py-0.5"><p className="text-[9px] text-white truncate text-center">{guides[idx].label}</p></div>}<button onClick={() => removeImage(idx)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition flex items-center justify-center shadow-md"><X className="h-3 w-3" /></button></div>
                            ))}
                            {Array.from({ length: Math.max(0, 7 - formData.images.length) }).map((_, i) => {
                              const slotIdx = formData.images.length + i; const slotGuide = guides[slotIdx];
                              return <label key={`empty-${i}`} htmlFor="image-upload" className="cursor-pointer"><div className={`w-full h-24 rounded-xl border-2 border-dashed flex flex-col items-center justify-center transition hover:border-orange-300 hover:bg-orange-50/40 ${slotIdx === guideSlide ? 'border-orange-400 bg-orange-50/50' : 'border-gray-200'}`}><span className="text-xl">{slotGuide?.icon || '📷'}</span><p className="text-[9px] text-gray-400 mt-1 text-center px-1 leading-tight">{slotGuide?.label || 'Photo'}</p></div></label>;
                            })}
                          </div>
                          {formData.images.length > 0 && <p className="text-xs text-green-600 font-medium">✓ {formData.images.length} photo{formData.images.length > 1 ? 's' : ''} uploaded{formData.images.length < 7 && ` · ${7 - formData.images.length} more recommended`}</p>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ══════════════════════════════════════════════════
                      STEP 5 – FEATURES
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 5 && (
                    <div className="space-y-8">
                      <p className="text-gray-500">Enhance your listing by detailing premium equipment.</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        {features.map(feature => {
                          const isSelected = formData.selectedFeatures.includes(feature.name);
                          return <div key={feature.id} onClick={() => { const updated = isSelected ? formData.selectedFeatures.filter(f => f !== feature.name) : [...formData.selectedFeatures, feature.name]; setFormData({ ...formData, selectedFeatures: updated }); }} className={`px-5 py-4 rounded-xl cursor-pointer transition-all border ${isSelected ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-100 text-gray-800 border-transparent hover:bg-gray-200'}`}><p className="text-sm font-medium">{feature.name}</p></div>;
                        })}
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 6 – PRICE & CONTACT
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 6 && (
                    <div className="bg-gray-50 p-4 sm:p-6 rounded-2xl">
                      <div className="space-y-6">
                        <div><Label className="text-xs text-gray-500 uppercase">Asking Price (Rupees)</Label><Input placeholder="₹ e.g. 45,00,000" className="h-12 text-base bg-gray-100 border-none" value={formData.price} onChange={(e) => setFormData({ ...formData, price: formatPrice(e.target.value) })} />{formData.price && <p className="text-xs text-green-600 mt-1">{formatToIndianUnit(formData.price)}</p>}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div><Label className="text-xs text-gray-500 uppercase">Contact Phone</Label><div className="flex"><div className="flex items-center px-3 bg-gray-100 rounded-l-md text-gray-500">+91</div><Input className="h-12 rounded-l-none" placeholder="9876543210" value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value.replace(/\D/g, '').slice(0, 10) })} /></div></div>
                          <div>
                            <Label className="text-xs text-gray-500 uppercase">Registration Number</Label>
                            <div className="flex gap-2 mb-2 mt-1">
                              <button type="button" onClick={() => { setFormData({ ...formData, registration_type: 'standard', registration_no: '' }); setRegNoExists(false); }} className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${formData.registration_type === 'standard' ? 'bg-orange-400 text-gray-700' : 'bg-gray-200 text-gray-700'}`}>Standard</button>
                              <button type="button" onClick={() => { setFormData({ ...formData, registration_type: 'bh', registration_no: '', rto_code: '' }); setRegNoExists(false); }} className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${formData.registration_type === 'bh' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}>BH Series</button>
                            </div>
                            {formData.registration_type === 'standard' ? (
                                  <div className="flex gap-2">
                                    <Select 
                                      value={formData.rto_code} 
                                      onValueChange={(v) => {
                                        setFormData({ ...formData, rto_code: v });
                                        checkRegistrationExists(v, formData.registration_no, 'standard');
                                      }}
                                    >
                                      <SelectTrigger className="w-24"><SelectValue placeholder="DL" /></SelectTrigger>
                                      <SelectContent>
                                        {rtoCodes.map(r => <SelectItem key={r.id} value={r.code}>{r.code}</SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                    <Input 
                                      placeholder="23 HG 6387" 
                                      value={formData.registration_no} 
                                      onChange={(e) => {
                                        const val = e.target.value.toUpperCase();
                                        setFormData({ ...formData, registration_no: val });
                                        checkRegistrationExists(formData.rto_code, val, 'standard');
                                      }} 
                                    />
                                  </div>
                                ) : (
                                  <Input 
                                    placeholder="22 BH 1234 AA" 
                                    className="h-12 tracking-wider" 
                                    value={formData.registration_no} 
                                    onChange={(e) => {
                                      const val = e.target.value.toUpperCase();
                                      setFormData({ ...formData, registration_no: val });
                                      checkRegistrationExists('', val, 'bh');
                                    }} 
                                  />
                                )}
                          </div>

                          {/* Registration number live check feedback */}
<div className="mt-1 min-h-[20px]">
  {regNoChecking && (
    <p className="text-xs text-gray-400 flex items-center gap-1">
      <Loader2 className="h-3 w-3 animate-spin" /> Checking registration number...
    </p>
  )}
  {!regNoChecking && regNoExists && formData.registration_no && (
    <p className="text-xs text-red-600 flex items-center gap-1">
      <AlertCircle className="h-3 w-3" />
      This registration number is already listed on our platform.
    </p>
  )}
  {!regNoChecking && !regNoExists && formData.registration_no && (formData.rto_code || formData.registration_type === 'bh') && (
    <p className="text-xs text-green-600 flex items-center gap-1">
      <CheckCircle2 className="h-3 w-3" /> Registration number is available.
    </p>
  )}
</div>

                          {/* Choice Number Toggle */}
<div className="mt-4">
  <Label className="text-xs text-gray-500 uppercase block mb-2">
    Is this a Choice / Fancy Number?
  </Label>
  <div className="flex gap-3">
    <button
      type="button"
      onClick={() => setFormData({ ...formData, is_choice_number: true })}
      className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all border-2 flex items-center justify-center gap-2 ${
        formData.is_choice_number === true
          ? 'bg-orange-500 border-orange-500 text-white shadow-md'
          : 'bg-white border-gray-200 text-gray-600 hover:border-orange-300 hover:bg-orange-50'
      }`}
    >
      <CheckCircle2 className="h-4 w-4" /> Yes
    </button>
    <button
      type="button"
      onClick={() => setFormData({ ...formData, is_choice_number: false })}
      className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all border-2 flex items-center justify-center gap-2 ${
        formData.is_choice_number === false
          ? 'bg-gray-500 border-gray-500 text-white shadow-md'
          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <X className="h-4 w-4" /> No
    </button>
  </div>
  {formData.is_choice_number === true && (
    <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
      <Star className="h-3 w-3" /> This will be shown as a Choice Number badge on your listing!
    </p>
  )}
</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 7 – VERIFY VEHICLE
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 7 && (
                    <div className="max-w-lg mx-auto">
                      <div className="relative rounded-2xl overflow-hidden mb-6 bg-gradient-to-br from-blue-600 to-indigo-700 p-8 text-white">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" /><div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-6 -translate-x-6" />
                        <div className="relative z-10">
                          <div className="flex items-center gap-3 mb-4"><div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"><Shield className="h-6 w-6 text-white" /></div><div><h2 className="text-xl font-bold">Verified by X7 Autoz</h2><p className="text-blue-200 text-sm">Build buyer trust instantly</p></div></div>
                          <ul className="space-y-2 text-sm text-blue-100 mb-4"><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-300" /> Physical inspection by certified partner</li><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-300" /> Verified badge on your listing</li><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-green-300" /> Higher buyer confidence & faster sale</li></ul>
                          {verificationPricing?.price > 0 ? <div className="bg-white/20 rounded-xl px-4 py-3 inline-block"><p className="text-xs text-blue-200">Verification fee</p><p className="text-2xl font-bold">₹{verificationPricing.price.toLocaleString('en-IN')}</p><p className="text-xs text-blue-200">+18% GST = ₹{(verificationPricing.price * 1.18).toFixed(0)}</p></div> : <div className="bg-white/20 rounded-xl px-4 py-3 inline-block"><p className="text-sm text-blue-200">Verification pricing not configured for this city</p></div>}
                        </div>
                      </div>
                      {verificationPricing?.price > 0 ? (
                        <div className="space-y-3">
                          <button onClick={() => { setWantVerification(true); setCurrentStep(s => s + 1); }} className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"><Shield className="h-5 w-5" /> Add Verification — ₹{(verificationPricing.price * 1.18).toFixed(0)} (incl. GST)</button>
                          <button onClick={() => { setWantVerification(false); setCurrentStep(s => s + 1); }} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-medium transition">Skip for now</button>
                        </div>
                      ) : <button onClick={() => setCurrentStep(s => s + 1)} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-medium transition">Continue</button>}
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 8 – FEATURE VEHICLE
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 8 && (
                    <div className="max-w-lg mx-auto">
                      {!featuredSlotsAvailable ? (
                        <div className="text-center py-8"><AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-secondary-500">Featured slots for {formData.city} are currently full.</p><p className="text-secondary-400 text-sm mt-1">Your vehicle will be listed normally.</p><button onClick={() => setCurrentStep(s => s + 1)} className="mt-6 w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-medium transition">Continue to Payment</button></div>
                      ) : (
                        <>
                          <div className="relative rounded-2xl overflow-hidden mb-6 bg-gradient-to-br from-orange-500 to-amber-500 p-8 text-white">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
                            <div className="relative z-10">
                              <div className="flex items-center gap-3 mb-4"><div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"><Star className="h-6 w-6 text-white fill-white" /></div><div><h2 className="text-xl font-bold">Feature Your Vehicle</h2><p className="text-orange-100 text-sm">Get seen by more buyers</p></div></div>
                              <ul className="space-y-2 text-sm text-orange-100 mb-4"><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-white" /> Displayed in Featured section on homepage</li><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-white" /> Auto-scrolling carousel visibility</li><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-white" /> "Featured" badge on your listing</li><li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-white" /> Up to 3× more views</li></ul>
                              {featuredPricing?.price > 0 ? <div className="bg-white/20 rounded-xl px-4 py-3 inline-block"><p className="text-xs text-orange-200">One-time feature fee</p><p className="text-2xl font-bold">₹{featuredPricing.price.toLocaleString('en-IN')}</p><p className="text-xs text-orange-200">+18% GST = ₹{(featuredPricing.price * 1.18).toFixed(0)}</p></div> : <div className="bg-white/20 rounded-xl px-4 py-3 inline-block"><p className="text-sm text-orange-100">Featured pricing not set for this city</p></div>}
                            </div>
                          </div>
                          {featuredPricing?.price > 0 ? (
                            <div className="space-y-3">
                              <button onClick={() => { setWantFeatured(true); setCurrentStep(s => s + 1); }} className="w-full py-4 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"><Star className="h-5 w-5 fill-white" /> Feature My Vehicle — ₹{(featuredPricing.price * 1.18).toFixed(0)} (incl. GST)</button>
                              <button onClick={() => { setWantFeatured(false); setCurrentStep(s => s + 1); }} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-medium transition">Skip — List normally</button>
                            </div>
                          ) : <button onClick={() => setCurrentStep(s => s + 1)} className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-medium transition">Continue to Payment</button>}
                        </>
                      )}
                    </div>
                  )}

                  {/* ══════════════════════════════════════════════════
                      STEP 9 – PAYMENT
                  ══════════════════════════════════════════════════ */}
                  {currentStep === 9 && cityQR && (() => {
                    const breakdown = getPriceBreakdown();
                    return (
                      <div className="max-w-lg mx-auto space-y-6">
                        <div className="text-center"><h2 className="text-2xl font-bold text-secondary-800">Complete Your Payment</h2><p className="text-gray-500 text-sm mt-1">Listing fee for {formData.city}</p></div>
                        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                          <div className="bg-gray-50 px-5 py-3 border-b"><p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Price Breakdown</p></div>
                          <div className="px-5 py-4 space-y-3">
                            <div className="flex justify-between items-center"><span className="text-sm text-gray-600">Listing Fee</span><span className="text-sm font-medium">₹{breakdown.base.toLocaleString('en-IN')}</span></div>
                            {wantVerification && breakdown.verificationAdd > 0 && <div className="flex justify-between items-center text-blue-600"><span className="text-sm flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" /> Verification Fee</span><span className="text-sm font-semibold">+ ₹{breakdown.verificationAdd.toLocaleString('en-IN')}</span></div>}
                            {wantFeatured && breakdown.featuredAdd > 0 && <div className="flex justify-between items-center text-orange-600"><span className="text-sm flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Featured Fee</span><span className="text-sm font-semibold">+ ₹{breakdown.featuredAdd.toLocaleString('en-IN')}</span></div>}
                            {appliedCoupon && <div className="flex justify-between items-center text-green-600"><span className="text-sm flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> Coupon ({appliedCoupon.code})</span><span className="text-sm font-semibold">− ₹{breakdown.discount.toLocaleString('en-IN')}</span></div>}
                            <div className="flex justify-between items-center text-gray-500"><span className="text-sm">GST (18%)</span><span className="text-sm">+ ₹{breakdown.gst.toLocaleString('en-IN')}</span></div>
                            <div className="flex justify-between items-center border-t pt-3"><span className="font-bold text-secondary-800">Total Payable</span><div className="text-right"><span className="text-2xl font-bold text-green-600">₹{breakdown.total.toLocaleString('en-IN')}</span>{appliedCoupon && <p className="text-xs text-green-600">You save ₹{breakdown.discount.toLocaleString('en-IN')}!</p>}</div></div>
                          </div>
                        </div>
                        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5">
                          <p className="text-sm font-semibold text-orange-800 mb-3 flex items-center gap-2"><Tag className="h-4 w-4" /> Have a discount coupon?</p>
                          {appliedCoupon ? (
                            <div className="flex items-center justify-between bg-green-100 border border-green-300 rounded-xl px-4 py-3"><div className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" /><div><p className="text-sm font-bold text-green-800 font-mono">{appliedCoupon.code}</p><p className="text-xs text-green-600">{appliedCoupon.message}</p></div></div><button onClick={removeCoupon} className="text-gray-400 hover:text-red-500"><X className="h-4 w-4" /></button></div>
                          ) : (
                            <div className="flex gap-2"><Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Enter coupon code" className="font-mono uppercase bg-white" onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(); }} /><Button onClick={applyCoupon} disabled={couponLoading || !couponInput.trim()} className="bg-orange-500 hover:bg-orange-600 text-white px-5">{couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}</Button></div>
                          )}
                        </div>
                        <Button onClick={handleRazorpayPayment} disabled={loading} className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 text-lg font-semibold rounded-xl shadow-lg">
                          {loading ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Processing...</> : <>Pay ₹{breakdown.total.toLocaleString('en-IN')} & Submit</>}
                        </Button>
                        <p className="text-center text-xs text-gray-400">Secured by Razorpay · Your vehicle will be reviewed after payment</p>
                      </div>
                    );
                  })()}
                  {currentStep === 9 && !cityQR && <div className="text-center py-12"><AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-3" /><p className="text-secondary-600">City pricing not configured. Please contact admin.</p></div>}

                  {/* Navigation */}
                  <div className="flex justify-between pt-6 border-t">
                    {currentStep > 1 && <Button variant="outline" onClick={handleBack}><ChevronLeft className="mr-2 h-4 w-4" />Back</Button>}
                    {currentStep < 7 && <Button onClick={handleNext} className="ml-auto bg-primary-500 text-black hover:bg-gray-100 border">Next<ChevronRight className="ml-2 h-4 w-4" /></Button>}
                    {/* {currentStep === 9 && <Button onClick={handleRazorpayPayment} disabled={loading} className="ml-auto bg-green-500 hover:bg-green-600 text-white">{loading ? 'Submitting...' : 'Submit for Approval'}<CheckCircle className="ml-2 h-4 w-4" /></Button>} */}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ══ VERIFICATION SCHEDULING MODAL ══ */}
        {showVerificationScheduleStep && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
              <div className="p-6 border-b bg-blue-600 rounded-t-2xl text-white">
                <div className="flex items-center gap-3"><div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center"><Shield className="h-5 w-5" /></div><div><p className="text-xs font-semibold text-blue-200 uppercase tracking-wider">Last Step</p><h2 className="text-xl font-bold">Schedule Vehicle Verification</h2><p className="text-sm text-blue-100 mt-0.5">Tell us when you're free so we can send a partner to verify your vehicle</p></div></div>
                {submittedVehicleId && <div className="mt-3 bg-white/10 rounded-lg px-3 py-2 text-xs text-blue-100">✅ Your payment was successful. Please complete this step to schedule the verification.</div>}
              </div>
              <div className="p-6 space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-800 mb-3">📅 Availability Option 1</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">Date *</label><input type="date" value={scheduleForm.slot1_date} min={todayStr()} onChange={(e) => { setScheduleForm(prev => ({ ...prev, slot1_date: e.target.value })); localStorage.setItem(LS_PENDING_SCHEDULE, JSON.stringify({ vehicleId: submittedVehicleId, scheduleForm: { ...scheduleForm, slot1_date: e.target.value } })); }} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />{isHoliday(scheduleForm.slot1_date) && <p className="text-red-500 text-xs mt-1">⚠️ This date is a holiday</p>}</div>
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">From Time</label><input type="time" min="10:00" max="16:30" value={scheduleForm.slot1_from} onChange={(e) => setScheduleForm(prev => ({ ...prev, slot1_from: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">To Time</label><input type="time" min="11:30" max="18:00" value={scheduleForm.slot1_to} onChange={(e) => setScheduleForm(prev => ({ ...prev, slot1_to: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                  </div>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
                  <h3 className="font-semibold text-indigo-800 mb-3">📅 Availability Option 2</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">Date *</label><input type="date" value={scheduleForm.slot2_date} min={todayStr()} onChange={(e) => { setScheduleForm(prev => ({ ...prev, slot2_date: e.target.value })); localStorage.setItem(LS_PENDING_SCHEDULE, JSON.stringify({ vehicleId: submittedVehicleId, scheduleForm: { ...scheduleForm, slot2_date: e.target.value } })); }} className="w-full border rounded-lg px-3 py-2 text-sm" />{isHoliday(scheduleForm.slot2_date) && <p className="text-red-500 text-xs mt-1">⚠️ This date is a holiday</p>}</div>
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">From Time</label><input type="time" min="10:00" max="16:30" value={scheduleForm.slot2_from} onChange={(e) => setScheduleForm(prev => ({ ...prev, slot2_from: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">To Time</label><input type="time" min="11:30" max="18:00" value={scheduleForm.slot2_to} onChange={(e) => setScheduleForm(prev => ({ ...prev, slot2_to: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">📍 Vehicle Location / Address</h3>
                  <div className="space-y-3">
                    <div><label className="text-xs text-gray-600 font-medium block mb-1">Street Address *</label><input className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" placeholder="House No, Street, Area" value={scheduleForm.address_street} onChange={(e) => setScheduleForm(prev => ({ ...prev, address_street: e.target.value }))} /></div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div><label className="text-xs text-gray-600 font-medium block mb-1">City</label><input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="City" value={scheduleForm.address_city} onChange={(e) => setScheduleForm(prev => ({ ...prev, address_city: e.target.value }))} /></div>
                      <div><label className="text-xs text-gray-600 font-medium block mb-1">State</label><input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="State" value={scheduleForm.address_state} onChange={(e) => setScheduleForm(prev => ({ ...prev, address_state: e.target.value }))} /></div>
                      <div><label className="text-xs text-gray-600 font-medium block mb-1">PIN Code *</label><input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="000000" maxLength={6} value={scheduleForm.address_pin} onChange={(e) => setScheduleForm(prev => ({ ...prev, address_pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} /></div>
                    </div>
                  </div>
                </div>
                <div><label className="text-xs text-gray-600 font-medium block mb-1">Your Contact Phone *</label><div className="flex"><span className="px-3 flex items-center bg-gray-100 border rounded-l-lg text-sm text-gray-500">+91</span><input className="flex-1 border border-l-0 rounded-r-lg px-3 py-2 text-sm" placeholder="9876543210" maxLength={10} value={scheduleForm.seller_phone} onChange={(e) => setScheduleForm(prev => ({ ...prev, seller_phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} /></div></div>
                <button onClick={handleSubmitSchedule} disabled={scheduleSubmitting} className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold rounded-xl text-lg transition flex items-center justify-center gap-2">
                  {scheduleSubmitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Submitting...</> : <><CheckCircle className="h-5 w-5" /> Submit Schedule & Finish</>}
                </button>
                <p className="text-center text-xs text-gray-400">Sundays and May 1st are non-working holidays. Our team will contact you to confirm.</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default UploadVehicleWizard;